import { useState, useEffect, useRef } from 'react';
import roundelSrc from '../assets/roundel.svg';
import roundelBlueSrc from '../assets/roundel-blue.svg';
import wayOutSrc from '../assets/wayout.svg';
import {
  getTeachers, removeTeacher,
  createStudent, updateStudentPin,
  getStudentsByParent, getStudentsByTeacher,
  getLatestSignoff, getSignoffHistory, createSignoff, removeSignoff,
  getActivityLog, getAssignments,
  getSignoffSettings, upsertSignoffSettings
} from '../lib/supabase';
import { fetchSignoffStatus, postSignoffAction } from '../lib/signoffAdmin';
import { daysUntil } from '../lib/underground';

export default function AdminPanel({ user, student, setStudent, onViewPlanner, onSignOut }) {
  const [tab, setTab] = useState('planner');
  const [students, setStudents] = useState(student ? [student] : []);
  const [activeStudent, setActiveStudent] = useState(student);
  const [teachers, setTeachers] = useState([]);
  const [latestSignoff, setLatestSignoff] = useState(null);
  const [signoffHistory, setSignoffHistory] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [signingOff, setSigningOff] = useState(false);
  const [signoffMsg, setSignoffMsg] = useState('');

  // Student setup
  const [studentName, setStudentName] = useState('');
  const [studentPin, setStudentPin] = useState('');
  const [setupError, setSetupError] = useState('');
  const [setupSaving, setSetupSaving] = useState(false);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [overviewAssignments, setOverviewAssignments] = useState([]);

  // Change PIN
  const [newPin, setNewPin] = useState('');
  const [pinMsg, setPinMsg] = useState('');

  // Daily sign-off email: status from /api/signoff-admin (parent only), kept
  // with the student it belongs to so a slow answer never lands on another.
  const [signoffStatus, setSignoffStatus] = useState(null);
  const [signoffStatusError, setSignoffStatusError] = useState(null);
  const signoffForbiddenFor = useRef(null);
  const signoffStatusLoadedFor = useRef(null);

  const [previewTeacher, setPreviewTeacher] = useState(() => sessionStorage.getItem('previewTeacher') === 'true');
  const isParent = user?.role === 'parent' && !previewTeacher;
  const isTeacher = user?.role === 'teacher' || (user?.role === 'parent' && previewTeacher);
  const isParentInPreview = user?.role === 'parent' && previewTeacher;

  function clearAndSignOut() {
    sessionStorage.removeItem('previewTeacher');
    onSignOut();
  }

  // Shared toggle so every header path gets the same handler
  const previewToggle = user?.role === 'parent'
    ? () => setPreviewTeacher(p => {
        const next = !p;
        sessionStorage.setItem('previewTeacher', next);
        return next;
      })
    : null;

  // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberate: previewTeacher must stay in this array (2026-04-30 bug); the load functions are re-created every render
  useEffect(() => { loadStudents(); }, [user, previewTeacher]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberate: reload only when the active student changes; the load functions are re-created every render
  useEffect(() => { if (activeStudent) { loadTeachers(); loadSignoffs(); loadActivity(); loadOverview(); } }, [activeStudent]);

  useEffect(() => {
    if (!activeStudent) return;
    const interval = setInterval(() => { loadOverview(); loadActivity(); loadSignoffs(); }, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberate: restart the 60 s timer only when the active student changes
  }, [activeStudent]);

  // Sign-off email status: once per student (the Overview/Activity names only
  // need its teachers list), again on opening Settings, then every 60 s only
  // while Settings is showing. The card also refreshes it after each save/action.
  useEffect(() => {
    if (!activeStudent) return;
    const onSettings = tab === 'settings';
    if (onSettings || signoffStatusLoadedFor.current !== activeStudent.id) {
      signoffStatusLoadedFor.current = activeStudent.id;
      loadSignoffStatus();
    }
    if (!onSettings) return;
    const interval = setInterval(loadSignoffStatus, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberate: runs when the student or the tab changes; loadSignoffStatus is re-created every render
  }, [activeStudent, tab]);

  async function loadOverview() {
    if (!activeStudent) return;
    try {
      const data = await getAssignments(activeStudent.id);
      setOverviewAssignments(data || []);
    } catch(e) { console.error('loadOverview:', e); }
  }

  async function loadStudents() {
    if (!user?.id) return;
    setLoadingStudents(true);
    let list = [];
    if (isParent) list = await getStudentsByParent(user.id);
    if (isTeacher) list = await getStudentsByTeacher(user.id);
    setStudents(list);
    if (list.length > 0 && !activeStudent) {
      setActiveStudent(list[0]);
      setStudent(list[0]);
    }
    setLoadingStudents(false);
  }

  async function loadTeachers() {
    if (!activeStudent) return;
    try {
      const data = await getTeachers(activeStudent.id);
      setTeachers(data);
    } catch(e) {
      console.error('loadTeachers:', e);
      setTeachers([]);
    }
  }

  async function loadSignoffs() {
    if (!activeStudent) return;
    try {
      const latest = await getLatestSignoff(activeStudent.id);
      const history = await getSignoffHistory(activeStudent.id, 7);
      setLatestSignoff(latest);
      setSignoffHistory(history);
    } catch(e) {
      console.error('loadSignoffs:', e);
    }
  }

  async function loadActivity() {
    if (!activeStudent) return;
    const log = await getActivityLog(activeStudent.id, 14);
    setActivityLog(log);
  }

  // Parents only. A 401/403 is final for that student: no retry, not even
  // on the 60-second refresh.
  async function loadSignoffStatus() {
    if (!activeStudent || user?.role !== 'parent') return;
    const studentId = activeStudent.id;
    if (signoffForbiddenFor.current === studentId) return;
    try {
      const status = await fetchSignoffStatus(studentId);
      setSignoffStatus({ ...status, studentId });
      setSignoffStatusError(null);
    } catch (e) {
      console.error('loadSignoffStatus:', e);
      const kind = e.kind === 'forbidden' ? 'forbidden' : 'failed';
      if (kind === 'forbidden') signoffForbiddenFor.current = studentId;
      setSignoffStatusError({ studentId, kind });
    }
  }

  const statusForStudent = signoffStatus && signoffStatus.studentId === activeStudent?.id ? signoffStatus : null;
  const linkedTeachers = statusForStudent?.teachers || [];

  function switchStudent(s) {
    setActiveStudent(s);
    setStudent(s);
    setSignoffMsg('');
  }

  async function handleRemoveTeacher(teacherId) {
    if (!window.confirm('Remove this teacher\'s access?')) return;
    await removeTeacher(teacherId, activeStudent.id);
    loadTeachers();
  }

  async function handleSetupStudent() {
    if (!studentName.trim()) { setSetupError('Enter a name.'); return; }
    if (!/^\d{4}$/.test(studentPin)) { setSetupError('PIN must be exactly 4 digits.'); return; }
    setSetupSaving(true); setSetupError('');
    try {
      const s = await createStudent(studentName.trim(), studentPin, user.id);
      setStudents(prev => [...prev, s]);
      setActiveStudent(s);
      setStudent(s);
      setShowAddStudent(false);
      setStudentName(''); setStudentPin('');
      setTab('planner');
    } catch { setSetupError('Error creating student profile — please try again.'); }
    finally { setSetupSaving(false); }
  }

  async function handleChangePin() {
    if (!/^\d{4}$/.test(newPin)) { setPinMsg('PIN must be 4 digits.'); return; }
    await updateStudentPin(activeStudent.id, newPin);
    setPinMsg('✅ PIN updated!');
    setNewPin('');
  }

  async function handleSignoff() {
    if (!activeStudent || !user?.id) return;
    setSigningOff(true);
    try {
      if (signedOffToday) {
        await removeSignoff(latestSignoff.id);
        setSignoffMsg('Sign-off removed.');
      } else {
        await createSignoff(activeStudent.id, user.id);
        setSignoffMsg('✅ Signed off!');
      }
      loadSignoffs();
    } catch { setSignoffMsg('Something went wrong — try again.'); }
    finally { setSigningOff(false); }
  }

  const signedOffToday = latestSignoff && new Date(latestSignoff.signed_off_at).toDateString() === new Date().toDateString();

  const siteUrl = window.location.origin;

  function fmtTime(ts) {
    return new Date(ts).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'America/Chicago'
    }) + ' CST';
  }

  function activityIcon(type) {
    if (type === 'login') return '🔑';
    if (type === 'assignment_added') return '📋';
    if (type === 'step_completed') return '✅';
    if (type === 'assignment_removed') return '🗑️';
    return '•';
  }

  if (loadingStudents) {
    return (
      <div>
        <AdminHeader user={user} onSignOut={clearAndSignOut} previewMode={previewTeacher} onPreviewToggle={previewToggle} />
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--muted)' }}>Loading…</div>
      </div>
    );
  }

  // ── PARENT: no students yet → first-time setup ────────────
  if (students.length === 0 && !showAddStudent && isParent) {
    return (
      <div>
        <AdminHeader user={user} onSignOut={clearAndSignOut} previewMode={previewTeacher} onPreviewToggle={previewToggle} />
        <div style={{ maxWidth: 640, margin: '0 auto', padding: 24 }}>
          <div className="section-label">First-time setup</div>
          <div className="card">
            <p style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 16 }}>
              Create your student's profile. You only do this once.
            </p>
            <SetupForm
              name={studentName} setName={setStudentName}
              pin={studentPin} setPin={setStudentPin}
              error={setupError} saving={setupSaving}
              onSave={handleSetupStudent}
            />
          </div>
        </div>
      </div>
    );
  }

  // ── TEACHER VIEW with no linked students ─────────────────
  // Two cases: an actual teacher (genuine onboarding) or a parent in preview
  // mode whose teacher-students list is empty. The parent case is a dead-end
  // unless we keep the eyeball toggle visible so they can flip back.
  if (students.length === 0 && !showAddStudent && isTeacher) {
    return (
      <div>
        <AdminHeader user={user} onSignOut={clearAndSignOut} previewMode={previewTeacher} onPreviewToggle={previewToggle} />
        <div style={{ textAlign: 'center', padding: 60, maxWidth: 520, margin: '0 auto' }}>
          {isParentInPreview ? (
            <>
              <p style={{ fontSize: 16, marginBottom: 8, fontWeight: 700 }}>No teacher-linked students</p>
              <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>
                You're previewing the teacher view, but no students have linked you as a teacher yet.
                Tap the eyeball above to switch back to your parent view.
              </p>
              <button className="btn btn-primary" onClick={previewToggle}>
                ← Back to Parent View
              </button>
            </>
          ) : (
            <>
              <p style={{ fontSize: 16, marginBottom: 8, fontWeight: 700 }}>You're all set.</p>
              <p style={{ fontSize: 13, color: 'var(--muted)' }}>You'll be linked to a student's planner shortly.</p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <AdminHeader user={user} onSignOut={clearAndSignOut} activeStudent={activeStudent} previewMode={previewTeacher} onPreviewToggle={previewToggle} />

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 16px' }}>

        {/* Student switcher */}
        {students.length > 0 && (
          <div style={{ display: 'flex', gap: 8, padding: '12px 0', overflowX: 'auto', alignItems: 'center' }}>
            {students.map(s => (
              <button key={s.id} onClick={() => switchStudent(s)} style={{
                padding: '6px 16px', borderRadius: 20, border: '1.5px solid',
                borderColor: activeStudent?.id === s.id ? 'var(--blue)' : 'var(--border)',
                background: activeStudent?.id === s.id ? 'var(--blue)' : 'transparent',
                color: activeStudent?.id === s.id ? 'white' : 'var(--muted)',
                fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap'
              }}>
                {s.name}
              </button>
            ))}
            {isParent && (
              <button onClick={() => setShowAddStudent(true)} style={{
                padding: '6px 16px', borderRadius: 20,
                border: '1.5px dashed var(--border)',
                background: 'transparent', color: 'var(--muted)',
                fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap'
              }}>
                + Add Student
              </button>
            )}
          </div>
        )}

        {/* Add student form */}
        {showAddStudent && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 14, color: 'var(--blue)' }}>New Student</div>
            <SetupForm
              name={studentName} setName={setStudentName}
              pin={studentPin} setPin={setStudentPin}
              error={setupError} saving={setupSaving}
              onSave={handleSetupStudent}
              onCancel={() => { setShowAddStudent(false); setStudentName(''); setStudentPin(''); setSetupError(''); }}
            />
          </div>
        )}

        {activeStudent && (
          <>
            {/* Tabs */}
            <div className="admin-tabs">
              {[
                ['planner', '📋 Planner'],
                isParent && ['teachers', '👩‍🏫 Teachers'],
                isTeacher && ['signoff', '✅ Sign-off'],
                isParent && ['overview', '🗺 Overview'],
                isParent && ['activity', '📊 Activity'],
                isParent && ['settings', '⚙️ Settings'],
              ].filter(Boolean).map(([id, label]) => (
                <button key={id} className={`admin-tab ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>
                  {label}
                </button>
              ))}
            </div>

            {/* PLANNER TAB */}
            {tab === 'planner' && (
              <div>
                {isParent && (
                  <div className="card" style={{ marginBottom: 16, borderLeft: `5px solid ${signedOffToday ? 'var(--green)' : '#f3a712'}` }}>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                      Teacher Check-in
                    </div>
                    {signedOffToday ? (
                      <div style={{ fontSize: 13, color: 'var(--green)' }}>
                        ✅ Checked by {signerLabel(latestSignoff, linkedTeachers)} at {new Date(latestSignoff?.signed_off_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    ) : (
                      <div style={{ fontSize: 13, color: '#c68a00' }}>
                        ⚠️ Not yet checked today
                        {latestSignoff && <span style={{ color: 'var(--muted)' }}> · Last check: {fmtTime(latestSignoff.signed_off_at)}</span>}
                      </div>
                    )}
                  </div>
                )}

                <div className="card" style={{ borderLeft: '5px solid var(--blue)' }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Viewing {activeStudent.name}'s planner</div>
                  <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>
                    {isParent ? 'As parent you can add, edit and remove any assignment.' : 'Review assignments, then sign off at the bottom of the planner.'}
                  </div>
                  <button className="btn btn-primary" onClick={onViewPlanner}>Open Planner →</button>
                </div>
              </div>
            )}

            {/* SIGN-OFF TAB (teacher only) */}
            {tab === 'signoff' && isTeacher && (
              <div>
                <div className="section-label">Daily sign-off</div>
                <div className="card" style={{ marginBottom: 16 }}>
                  <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>
                    After reviewing {activeStudent.name}'s assignments together, tap below to confirm today's check-in.
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <button
                      onClick={handleSignoff}
                      disabled={signingOff}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        gap: 4, padding: '4px 8px',
                        opacity: signingOff ? 0.5 : 1, transition: 'opacity 0.2s',
                      }}
                      title={signedOffToday ? "Tap to undo today's sign-off" : `Sign off ${activeStudent.name}'s planner for today`}>
                      <svg width="52" height="52" viewBox="0 0 44 44" style={{ transition: 'all 0.25s' }}>
                        {signedOffToday ? (
                          <>
                            <circle cx="22" cy="22" r="18" fill="#00782a"/>
                            <polyline points="13,22 19,28 31,15" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                          </>
                        ) : (
                          <circle cx="22" cy="22" r="18" fill="none" stroke="#aaa" strokeWidth="2.5"/>
                        )}
                      </svg>
                      <span style={{ fontSize: 10, fontWeight: 700, color: signedOffToday ? '#00782a' : 'var(--muted)', letterSpacing: '0.5px', fontFamily: 'p22-underground, Arial' }}>
                        {signingOff ? '…' : signedOffToday ? 'SIGNED OFF' : 'SIGN OFF'}
                      </span>
                    </button>
                    {signedOffToday && (
                      <div style={{ fontSize: 13, color: 'var(--green)', fontWeight: 600 }}>
                        Today at {new Date(latestSignoff.signed_off_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 400, marginTop: 2 }}>Tap to undo</div>
                      </div>
                    )}
                  </div>
                  {signoffMsg && <div style={{ fontSize: 13, marginTop: 8, color: 'var(--green)' }}>{signoffMsg}</div>}
                </div>

                <div className="section-label">Recent sign-offs</div>
                {signoffHistory.length === 0 && (
                  <div className="card" style={{ color: 'var(--muted)', fontSize: 14 }}>No sign-offs recorded yet.</div>
                )}
                {signoffHistory.map(s => (
                  <div key={s.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontSize: 22 }}>✅</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{fmtTime(s.signed_off_at)}</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)' }}>by {s.users?.name || 'Teacher'}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* TEACHERS TAB (parent only) */}
            {tab === 'teachers' && isParent && (
              <div>
                <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
                  Teachers are added by Sho — ask her.
                </p>

                <div className="section-label">Current teachers ({teachers.length})</div>
                {teachers.length === 0 && (
                  <div className="card" style={{ color: 'var(--muted)', fontSize: 14 }}>No teachers added yet.</div>
                )}
                {teachers.map(t => (
                  <div key={t.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--blue)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, flexShrink: 0 }}>
                      {(t.users?.name || t.users?.email || '?')[0].toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{t.users?.name || 'Pending sign-in'}</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)' }}>{t.users?.email}</div>
                    </div>
                    <button className="btn btn-sm" style={{ background: 'none', color: 'var(--red)', border: 'none', fontWeight: 700, cursor: 'pointer' }}
                      onClick={() => handleRemoveTeacher(t.teacher_id)}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* OVERVIEW TAB (parent only) */}
            {tab === 'overview' && isParent && (
              <div>
                {(() => {
                  const getZone = a => {
                    const steps = a.steps || [];
                    const allDone = steps.length > 0 && steps.every(s => s.done);
                    if (allDone) return 'done';
                    const d = daysUntil(a.due_date);
                    if (d < 0) return 'overdue';
                    if (d <= 2) return 'urgent';
                    if (d <= 6) return 'upcoming';
                    return 'distant';
                  };
                  const active = overviewAssignments.filter(a => getZone(a) !== 'done');
                  const zones = [
                    { id: 'overdue',  label: 'OVERDUE',    color: '#7b0000', rule: 'Past due date',      assignments: active.filter(a => getZone(a) === 'overdue') },
                    { id: 'urgent',   label: 'URGENT',     color: '#e32017', rule: '2 days or less',     assignments: active.filter(a => getZone(a) === 'urgent') },
                    { id: 'upcoming', label: 'DUE SOON',   color: '#c68a00', rule: '3–6 days',           assignments: active.filter(a => getZone(a) === 'upcoming') },
                    { id: 'distant',  label: 'NEXT WEEK+', color: '#00782a', rule: '7 or more days',     assignments: active.filter(a => getZone(a) === 'distant') },
                  ];

                  return (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 20 }}>
                        {zones.map(z => (
                          <div key={z.id} style={{ borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }}>
                            <div style={{ background: z.color, padding: '6px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ color: 'white', fontSize: 11, fontWeight: 700, letterSpacing: '0.8px', fontFamily: 'p22-underground, Arial' }}>{z.label}</span>
                            </div>
                            <div style={{ background: 'white', padding: '6px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                              <span style={{ fontSize: 28, fontWeight: 700, color: z.color, lineHeight: 1, fontFamily: 'p22-underground, Arial' }}>{z.assignments.length}</span>
                              <span style={{ fontSize: 8, color: '#888', textAlign: 'right', lineHeight: 1.3 }}>{z.rule}</span>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 20 }}>
                        <div className="card" style={{ borderLeft: '5px solid #003688', padding: '10px 14px' }}>
                          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: '#888', marginBottom: 4 }}>Last Login</div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#003688' }}>
                            {latestSignoff?.signed_off_at
                              ? new Date(latestSignoff.signed_off_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Chicago' }) + ' CST'
                              : 'No data yet'}
                          </div>
                        </div>
                        <div className="card" style={{ borderLeft: `5px solid ${latestSignoff ? '#00782a' : '#ccc'}`, padding: '10px 14px' }}>
                          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: '#888', marginBottom: 4 }}>Last Teacher Check-in</div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: latestSignoff ? '#00782a' : '#999', overflowWrap: 'anywhere' }}>
                            {latestSignoff
                              ? `${signerLabel(latestSignoff, linkedTeachers)} · ${new Date(latestSignoff.signed_off_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'America/Chicago' })}`
                              : 'No check-in yet'}
                          </div>
                          {latestSignoff?.note && <SignoffNote note={latestSignoff.note} />}
                        </div>
                      </div>

                      {zones.filter(z => z.assignments.length > 0).map(z => (
                        <div key={z.id} style={{ marginBottom: 20 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.2px', textTransform: 'uppercase', color: z.color, marginBottom: 8, borderLeft: `3px solid ${z.color}`, paddingLeft: 8 }}>
                            {z.label} — {z.assignments.length} assignment{z.assignments.length !== 1 ? 's' : ''}
                          </div>
                          {z.assignments.map(a => {
                            const steps = a.steps || [];
                            const done = steps.filter(s => s.done).length;
                            const pct = steps.length ? Math.round(done / steps.length * 100) : 0;
                            const d = daysUntil(a.due_date);
                            return (
                              <div key={a.id} className="card" style={{ borderLeft: `5px solid ${a.color}`, marginBottom: 8 }}>
                                {z.id === 'overdue' && <div style={{ background: 'repeating-linear-gradient(45deg,#7b0000,#7b0000 5px,#a00000 5px,#a00000 10px)', height: 5, margin: '-14px -14px 10px', borderRadius: '8px 8px 0 0' }}/>}
                                {z.id === 'urgent' && <div style={{ background: 'repeating-linear-gradient(45deg,#f3a712,#f3a712 5px,#1a1a1a 5px,#1a1a1a 10px)', height: 5, margin: '-14px -14px 10px', borderRadius: '8px 8px 0 0' }}/>}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                                  <div>
                                    <div style={{ fontWeight: 700, fontSize: 15 }}>{a.name}</div>
                                    <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                                      <span style={{ color: a.color, fontWeight: 700 }}>●</span> {a.subject} · Due {new Date(a.due_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                    </div>
                                  </div>
                                  <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10, background: z.color, color: 'white', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                    {d < 0 ? `${Math.abs(d)}d overdue` : d === 0 ? 'Due today' : d === 1 ? '1 day left' : `${d} days left`}
                                  </span>
                                </div>
                                {steps.length > 0 && (
                                  <>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                                      {steps.map((s, si) => (
                                        <span key={si} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8, border: `1.5px solid ${a.color}`, color: s.done ? 'white' : a.color, background: s.done ? a.color : 'transparent', fontWeight: 600 }}>
                                          {s.done ? '✓ ' : ''}{s.label}
                                        </span>
                                      ))}
                                    </div>
                                    <div style={{ background: '#eee', borderRadius: 3, height: 4, marginBottom: 2 }}>
                                      <div style={{ width: `${pct}%`, background: a.color, height: 4, borderRadius: 3 }}/>
                                    </div>
                                    <div style={{ fontSize: 10, color: '#999' }}>{done}/{steps.length} steps done</div>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ))}

                      {active.length === 0 && (
                        <div className="empty-state"><div className="icon">🎉</div><p>All clear — no active assignments.</p></div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {/* ACTIVITY TAB (parent only) */}
            {tab === 'activity' && isParent && (
              <div>
                <div className="section-label">Teacher sign-off history</div>
                {signoffHistory.length === 0 && (
                  <div className="card" style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 16 }}>No sign-offs recorded yet.</div>
                )}
                {signoffHistory.map(s => (
                  <div key={s.id} className="card" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                    <div style={{ fontSize: 22 }}>✅</div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{fmtTime(s.signed_off_at)}</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)', overflowWrap: 'anywhere' }}>
                        by {signerLabel(s, linkedTeachers)}
                        <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, letterSpacing: '0.5px', padding: '1px 6px', borderRadius: 8, background: '#eee', color: 'var(--muted)' }}>
                          {s.signed_by_email ? 'by email' : 'logged in'}
                        </span>
                      </div>
                      {s.note && <SignoffNote note={s.note} />}
                    </div>
                  </div>
                ))}

                <div className="section-label" style={{ marginTop: 20 }}>Student activity log</div>
                {activityLog.length === 0 && (
                  <div className="card" style={{ color: 'var(--muted)', fontSize: 14 }}>No activity recorded yet.</div>
                )}
                {activityLog.map(a => (
                  <div key={a.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', marginBottom: 8 }}>
                    <div style={{ fontSize: 20, flexShrink: 0 }}>{activityIcon(a.event_type)}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{a.description}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{fmtTime(a.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* SETTINGS TAB (parent only) */}
            {tab === 'settings' && isParent && (
              <div>
                <div className="section-label">How {activeStudent.name} signs in</div>
                <div className="card" style={{ marginBottom: 16 }}>
                  <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 10 }}>
                    Go to this address and sign in with {activeStudent.name}'s Google account or email and password. The planner then locks behind their PIN.
                  </p>
                  <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 12px', fontSize: 13, fontFamily: 'monospace', wordBreak: 'break-all', border: '1px solid var(--border)', marginBottom: 8 }}>
                    {siteUrl}
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => { navigator.clipboard.writeText(siteUrl); alert('Copied!'); }}>
                    Copy URL
                  </button>
                  <p style={{ fontSize: 12, color: 'var(--muted)', opacity: 0.8, marginTop: 10 }}>
                    {activeStudent.email ? `Signs in as: ${activeStudent.email}` : 'No sign-in email registered yet'}
                  </p>
                </div>

                <div className="section-label">Change PIN for {activeStudent.name}</div>
                <div className="card">
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 14, background: 'var(--bg)', color: 'var(--text)' }}
                      type="password" inputMode="numeric" maxLength={4}
                      placeholder="New 4-digit PIN"
                      value={newPin}
                      onChange={e => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    />
                    <button className="btn btn-primary" onClick={handleChangePin}>Update PIN</button>
                  </div>
                  {pinMsg && <div style={{ fontSize: 13, marginTop: 8, color: 'var(--green)' }}>{pinMsg}</div>}
                </div>

                <SignoffEmailCard
                  key={activeStudent.id}
                  student={activeStudent}
                  parentEmail={user?.email || statusForStudent?.parentEmails?.[0] || ''}
                  status={statusForStudent}
                  statusError={signoffStatusError?.studentId === activeStudent.id ? signoffStatusError.kind : null}
                  onStatusRefresh={loadSignoffStatus}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── SHARED SUB-COMPONENTS ─────────────────────────────────────

function AdminRoundel() {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <svg style={{ width: 40, height: 40, flexShrink: 0 }} viewBox="0 0 52 52">
        <circle cx="26" cy="26" r="24" fill="#e32017"/>
        <rect x="2" y="19" width="48" height="14" fill="#003688"/>
        <text x="26" y="29" textAnchor="middle" fontSize="8" fill="white" fontWeight="700" fontFamily="Arial">HOMEWORK</text>
      </svg>
    );
  }
  return (
    <img
      src={roundelSrc}
      alt="Homework Underground"
      width={40}
      height={40}
      style={{ flexShrink: 0, display: 'block' }}
      onError={() => setFailed(true)}
    />
  );
}

function AdminWayOutButton({ onClick }) {
  const [failed, setFailed] = useState(false);
  return (
    <button
      className="btn btn-ghost btn-sm"
      style={{ padding: '2px 4px', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}
      onClick={onClick}
    >
      {failed ? (
        <span style={{ color: 'white', fontSize: 13, fontWeight: 700, border: '1px solid rgba(255,255,255,0.4)', padding: '4px 10px', borderRadius: 6 }}>Way out</span>
      ) : (
        <img
          src={wayOutSrc}
          alt="Way out"
          height="28"
          style={{ display: 'block' }}
          onError={() => setFailed(true)}
        />
      )}
    </button>
  );
}

function AdminPreviewToggle({ previewMode, onClick }) {
  const [failed, setFailed] = useState(false);
  return (
    <button
      onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 12, padding: '4px 10px', color: 'white', fontWeight: 700, cursor: 'pointer' }}
    >
      {failed ? (
        <span>👁</span>
      ) : (
        <img
          src={roundelBlueSrc}
          alt=""
          width={16}
          height={16}
          style={{ display: 'block' }}
          onError={() => setFailed(true)}
        />
      )}
      {previewMode ? 'Parent view' : 'Teacher view'}
    </button>
  );
}

function AdminHeader({ user, onSignOut, activeStudent, previewMode, onPreviewToggle }) {
  return (
    <div className="admin-header" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px' }}>
      <AdminRoundel />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 17, color: 'white' }}>HOMEWORK UNDERGROUND</div>
        <div style={{ fontSize: 11, opacity: 0.7, color: 'white' }}>
          {user?.name || user?.email} · {user?.role === 'parent' ? 'Parent' : 'Teacher'}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {onPreviewToggle && (
          <AdminPreviewToggle previewMode={previewMode} onClick={onPreviewToggle} />
        )}
        <AdminWayOutButton onClick={onSignOut} />
      </div>
    </div>
  );
}

function SetupForm({ name, setName, pin, setPin, error, saving, onSave, onCancel }) {
  return (
    <>
      <div className="form-group">
        <label>Student's First Name</label>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Finn" />
      </div>
      <div className="form-group">
        <label>4-Digit PIN</label>
        <input
          type="password" inputMode="numeric" maxLength={4}
          value={pin}
          onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
          placeholder="Choose a 4-digit PIN"
        />
        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
          Your student uses this to access their planner on any device.
        </div>
      </div>
      {error && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 8 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        {onCancel && <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>}
        <button className="btn btn-primary" onClick={onSave} disabled={saving}>
          {saving ? 'Creating…' : 'Create Student Profile →'}
        </button>
      </div>
    </>
  );
}

// ── DAILY SIGN-OFF EMAIL ──────────────────────────────────────

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_COPY_RECIPIENTS = 10;
const AMBER = '#c68a00';

// Who signed, for a parent: the teacher's name when readable; for an email
// sign-off the linked teacher's name matching the address, else the address.
// Never a bare "Teacher" for an email sign-off.
function signerLabel(signoff, teachers) {
  if (!signoff) return '';
  if (signoff.users?.name) return signoff.users.name;
  if (signoff.signed_by_email) return nameForEmail(signoff.signed_by_email, teachers);
  return 'Teacher (logged in)';
}

function nameForEmail(email, teachers) {
  const e = (email || '').toLowerCase();
  const match = (teachers || []).find(t => (t.email || '').toLowerCase() === e);
  return match?.name || email;
}

function chicagoToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Chicago' }).format(new Date());
}

// 'YYYY-MM-DD' → "Wed 30 Sep" (formatted at noon UTC so no zone can shift the day).
// Built from en-US parts: en-GB now abbreviates September as "Sept".
function fmtLogDate(isoDate) {
  const [y, m, d] = isoDate.split('-').map(Number);
  const parts = {};
  for (const p of new Intl.DateTimeFormat('en-US', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
    .formatToParts(new Date(Date.UTC(y, m - 1, d, 12)))) parts[p.type] = p.value;
  return `${parts.weekday} ${parts.day} ${parts.month}`;
}

// Timestamp → "2:31 PM" in Chicago.
function fmtChicagoTime(ts) {
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago' }).format(new Date(ts));
}

// Split a typed list, lowercase, de-duplicate. Returns { list, bad }.
function parseAddressList(value) {
  const list = [];
  const bad = [];
  for (const part of (value || '').split(/[,;\s]+/)) {
    const email = part.trim().toLowerCase();
    if (!email) continue;
    if (!EMAIL_SHAPE.test(email)) bad.push(part);
    else if (!list.includes(email)) list.push(email);
  }
  return { list, bad };
}

function SignoffNote({ note }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 400, fontStyle: 'italic', color: 'var(--text)', marginTop: 4, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
      “{note}”
    </div>
  );
}

// One line of the endpoint's answer, in our own words. Never raw server text.
function actionMessage(action, result, teachers) {
  const to = (email) => nameForEmail(email, teachers);
  if (action === 'preview') {
    if (result.status === 'sent') return { tone: 'ok', text: `Preview sent to ${result.to}` };
    return { tone: 'bad', text: `Couldn't send the preview: ${result.reason || 'email send failed'}. Try again.` };
  }
  if (result.status === 'sent') {
    const n = result.open_assignments;
    let text = `Sent to ${to(result.recipient)} (${n} open assignment${n === 1 ? '' : 's'})`;
    if (result.copies_sent) text += ` · ${result.copies_sent} review cop${result.copies_sent === 1 ? 'y' : 'ies'}`;
    if (result.copies_failed) text += ` · ${result.copies_failed} cop${result.copies_failed === 1 ? 'y' : 'ies'} failed`;
    return { tone: 'ok', text };
  }
  if (result.status === 'skipped') {
    if (result.reason === 'already sent today') {
      const when = result.sentAt ? ` at ${fmtChicagoTime(result.sentAt)}` : '';
      const who = result.recipient ? ` to ${to(result.recipient)}` : '';
      return { tone: 'muted', text: `Already sent today${when}${who}` };
    }
    if (result.reason === 'paused') return { tone: 'warn', text: 'Paused — resume first' };
    return { tone: 'warn', text: 'No recipient set' };
  }
  return { tone: 'bad', text: `Couldn't send: ${result.reason || 'email send failed'}. Check the recipient address, then try again.` };
}

const TONE_COLOR = { ok: 'var(--green)', warn: AMBER, bad: 'var(--red)', muted: 'var(--muted)' };

const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 14, background: 'var(--bg)', color: 'var(--text)' };
const fieldLabel = { display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6 };
const hint = { fontSize: 12, color: 'var(--muted)', marginTop: 4 };

function SignoffEmailCard({ student, parentEmail, status, statusError, onStatusRefresh }) {
  const [settings, setSettings] = useState(null);
  const [settingsState, setSettingsState] = useState('loading'); // loading | ready | error

  const [recipientDraft, setRecipientDraft] = useState(null);
  const [otherMode, setOtherMode] = useState(false);
  const [copyDraft, setCopyDraft] = useState(null);
  const [overrideDraft, setOverrideDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [overrideMsg, setOverrideMsg] = useState(null);
  const [busyAction, setBusyAction] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);

  useEffect(() => {
    let live = true;
    getSignoffSettings(student.id)
      .then(row => { if (live) { setSettings(row); setSettingsState('ready'); } })
      .catch(e => { console.error('getSignoffSettings:', e); if (live) setSettingsState('error'); });
    return () => { live = false; };
  }, [student.id]);

  const teachers = status?.teachers || [];
  const today = chicagoToday();
  const paused = !!settings?.paused;

  // Default recipient: a linked teacher from the list, or "someone else".
  const savedRecipient = settings?.default_recipient_email || '';
  const recipientValue = recipientDraft ?? savedRecipient;
  const recipientNorm = recipientValue.trim().toLowerCase();
  const recipientIsTeacher = teachers.some(t => t.email === recipientNorm);
  const selectValue = otherMode || (recipientNorm && !recipientIsTeacher) ? '__other__' : recipientNorm;

  const savedCopies = settings?.copy_recipients || '';
  const copyValue = copyDraft ?? savedCopies;
  const overrideToday = settings?.override_date === today && settings?.override_email ? settings.override_email : null;

  function flash(setter, msg) {
    setter(msg);
    setTimeout(() => setter(current => (current === msg ? null : current)), 4000);
  }

  async function save(patch) {
    const row = await upsertSignoffSettings(student.id, patch);
    setSettings(row);
    onStatusRefresh();
    return row;
  }

  async function handleSave() {
    const recipient = recipientValue.trim().toLowerCase();
    if (recipient && !EMAIL_SHAPE.test(recipient)) { setSaveMsg({ tone: 'bad', text: 'Check the address' }); return; }
    const { list, bad } = parseAddressList(copyValue);
    if (bad.length) { setSaveMsg({ tone: 'bad', text: 'Check the addresses' }); return; }
    if (list.length > MAX_COPY_RECIPIENTS) { setSaveMsg({ tone: 'bad', text: `Up to ${MAX_COPY_RECIPIENTS} copy addresses` }); return; }
    setSaving(true);
    try {
      const row = await save({
        default_recipient_email: recipient || null,
        copy_recipients: list.length ? list.join(', ') : null,
      });
      setRecipientDraft(null);
      setCopyDraft(null);
      setOtherMode(false);
      flash(setSaveMsg, { tone: 'ok', text: row.default_recipient_email ? `Saved — emails go to ${row.default_recipient_email}` : 'Saved — no recipient set' });
    } catch (e) {
      console.error('save signoff settings:', e);
      setSaveMsg({ tone: 'bad', text: "Couldn't save — try again" });
    } finally { setSaving(false); }
  }

  async function handleUseToday() {
    const email = overrideDraft.trim().toLowerCase();
    if (!EMAIL_SHAPE.test(email)) { setOverrideMsg({ tone: 'bad', text: 'Check the address' }); return; }
    setSaving(true);
    try {
      await save({ override_email: email, override_date: chicagoToday() });
      setOverrideDraft('');
      flash(setOverrideMsg, { tone: 'ok', text: 'Saved' });
    } catch (e) {
      console.error('save override:', e);
      setOverrideMsg({ tone: 'bad', text: "Couldn't save — try again" });
    } finally { setSaving(false); }
  }

  async function handleClearOverride() {
    setSaving(true);
    try {
      await save({ override_email: null, override_date: null });
      setOverrideDraft('');
      flash(setOverrideMsg, { tone: 'ok', text: 'Cleared' });
    } catch (e) {
      console.error('clear override:', e);
      setOverrideMsg({ tone: 'bad', text: "Couldn't save — try again" });
    } finally { setSaving(false); }
  }

  async function handlePause(next) {
    setSaving(true);
    try { await save({ paused: next }); }
    catch (e) { console.error('save pause:', e); flash(setSaveMsg, { tone: 'bad', text: "Couldn't save — try again" }); }
    finally { setSaving(false); }
  }

  async function runAction(action) {
    if (action === 'send_now') {
      const to = overrideToday || savedRecipient;
      const ok = window.confirm(
        to
          ? `Send today's sign-off email to ${to} now? It counts as today's email, so the 2pm one won't go out again.`
          : "Send today's sign-off email now?"
      );
      if (!ok) return;
    }
    setBusyAction(action);
    setActionMsg(null);
    try {
      const result = await postSignoffAction(student.id, action);
      setActionMsg(actionMessage(action, result, teachers));
      onStatusRefresh();
    } catch (e) {
      console.error('signoff action:', e);
      setActionMsg(e.kind === 'forbidden'
        ? { tone: 'bad', text: 'Only a parent can use this.' }
        : { tone: 'bad', text: "Couldn't send — try again" });
    } finally { setBusyAction(null); }
  }

  const pastCutoff = status && (status.chicagoHour > 14 || (status.chicagoHour === 14 && status.chicagoMinute >= 5));

  function logStatus(entry) {
    if (entry.signed?.byEmail) {
      return { tone: 'ok', text: `Signed ${fmtChicagoTime(entry.signed.at)}${entry.signed.hasNote ? ' · note' : ''}` };
    }
    if (entry.signed) return { tone: 'ok', text: 'Signed (logged in)' };
    if (entry.sentTo) return { tone: 'warn', text: 'Not signed' };
    if (entry.date === status.today) {
      if (paused) return { tone: 'muted', text: 'Paused' };
      if (status.isSchoolDay && pastCutoff) return { tone: 'warn', text: 'Not sent', canSend: true };
      return { tone: 'muted', text: 'Due at 2pm' };
    }
    return { tone: 'muted', text: 'Not sent' };
  }

  const forbidden = statusError === 'forbidden';
  const disabled = saving || settingsState !== 'ready';

  return (
    <>
      <div className="section-label">Daily sign-off email</div>
      <div className="card">
        <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>
          Sent on school days at 2:00pm with {student.name}'s open assignments and a one-click sign-off.
        </p>

        {paused && (
          <div style={{ fontSize: 13, fontWeight: 700, color: AMBER, marginBottom: 14 }}>Paused — no emails until you resume.</div>
        )}
        {settingsState === 'error' && (
          <div style={{ fontSize: 13, color: 'var(--red)', marginBottom: 14 }}>Couldn't load the email settings — reload to try again.</div>
        )}

        {/* 1. Default recipient */}
        <div style={{ marginBottom: 16 }}>
          <label style={fieldLabel} htmlFor="signoff-recipient">Send to</label>
          <select
            id="signoff-recipient"
            style={inputStyle}
            value={selectValue}
            disabled={disabled}
            onChange={e => {
              const v = e.target.value;
              if (v === '__other__') { setOtherMode(true); setRecipientDraft(recipientIsTeacher ? '' : recipientValue); }
              else { setOtherMode(false); setRecipientDraft(v); }
            }}
          >
            {!recipientNorm && selectValue !== '__other__' && <option value="">Choose a teacher…</option>}
            {teachers.map(t => (
              <option key={t.email} value={t.email}>{t.name ? `${t.name} (${t.email})` : t.email}</option>
            ))}
            <option value="__other__">Someone else by email…</option>
          </select>
          {selectValue === '__other__' && (
            <input
              style={{ ...inputStyle, marginTop: 8 }}
              type="email"
              placeholder="name@school.org"
              value={recipientValue}
              disabled={disabled}
              onChange={e => setRecipientDraft(e.target.value)}
            />
          )}
        </div>

        {/* 2. Just for today */}
        <div style={{ marginBottom: 16 }}>
          <label style={fieldLabel} htmlFor="signoff-override">Just for today</label>
          {overrideToday && (
            <div style={{ fontSize: 13, color: 'var(--blue)', fontWeight: 600, marginBottom: 6, overflowWrap: 'anywhere' }}>
              Today's email goes to {overrideToday} instead.
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              id="signoff-override"
              style={{ ...inputStyle, flex: '1 1 200px', width: 'auto' }}
              type="email"
              placeholder="A substitute's email"
              value={overrideDraft}
              disabled={disabled}
              onChange={e => setOverrideDraft(e.target.value)}
            />
            <button className="btn btn-ghost btn-sm" disabled={disabled} onClick={handleUseToday}>Use today</button>
            <button className="btn btn-ghost btn-sm" disabled={disabled || !settings?.override_email} onClick={handleClearOverride}>Clear</button>
          </div>
          <div style={hint}>Set it before 2pm. It stops applying at midnight.</div>
          {overrideMsg && <div style={{ fontSize: 13, marginTop: 6, color: TONE_COLOR[overrideMsg.tone] }}>{overrideMsg.text}</div>}
        </div>

        {/* 3. Pause */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, fontWeight: 600, marginBottom: 16, cursor: disabled ? 'default' : 'pointer' }}>
          <input type="checkbox" checked={paused} disabled={disabled} onChange={e => handlePause(e.target.checked)} style={{ width: 18, height: 18 }} />
          Pause emails (holidays)
        </label>

        {/* 8d. Review copies */}
        <div style={{ marginBottom: 12 }}>
          <label style={fieldLabel} htmlFor="signoff-copies">Also send a copy, without the button, to</label>
          <input
            id="signoff-copies"
            style={inputStyle}
            type="text"
            inputMode="email"
            placeholder="head@school.org"
            value={copyValue}
            disabled={disabled}
            onChange={e => setCopyDraft(e.target.value)}
          />
          <div style={hint}>
            For anyone who should see the daily list but not sign it, e.g. the head of middle school. Separate addresses with commas.
          </div>
          {savedCopies && (
            <div style={{ fontSize: 12, color: 'var(--text)', marginTop: 6, overflowWrap: 'anywhere' }}>
              Copies go to: {savedCopies}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <button className="btn btn-primary btn-sm" disabled={disabled} onClick={handleSave}>{saving ? 'Saving…' : 'Save'}</button>
          {saveMsg && <span style={{ fontSize: 13, color: TONE_COLOR[saveMsg.tone], overflowWrap: 'anywhere' }}>{saveMsg.text}</span>}
        </div>
      </div>

      {/* 4 + 5. Log and buttons */}
      <div className="section-label">Last 5 school days</div>
      <div className="card">
        {forbidden && <div style={{ fontSize: 13, color: 'var(--red)' }}>Only a parent can use this.</div>}
        {!forbidden && !status && (
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>
            {statusError === 'failed' ? "Couldn't load the log — it will try again in a minute." : 'Loading…'}
          </div>
        )}
        {!forbidden && status && (
          <>
            {status.log.map(entry => {
              const st = logStatus(entry);
              return (
                <div key={entry.date} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid var(--border)', fontSize: 13 }}>
                  <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    <span style={{ fontWeight: 700 }}>{fmtLogDate(entry.date)}</span>
                    <span style={{ color: 'var(--muted)' }}>{entry.sentTo ? ` · sent to ${nameForEmail(entry.sentTo, teachers)}` : ' · not sent'}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <span style={{ fontWeight: 700, color: TONE_COLOR[st.tone], whiteSpace: 'nowrap' }}>{st.text}</span>
                    {st.canSend && (
                      <button className="btn btn-ghost btn-sm" disabled={!!busyAction} onClick={() => runAction('send_now')}>Send now</button>
                    )}
                  </div>
                </div>
              );
            })}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
              <button className="btn btn-ghost btn-sm" disabled={!!busyAction} onClick={() => runAction('preview')}>
                {busyAction === 'preview' ? 'Sending…' : `Send me a preview${parentEmail ? ` (to ${parentEmail})` : ''}`}
              </button>
              <button className="btn btn-primary btn-sm" disabled={!!busyAction} onClick={() => runAction('send_now')}>
                {busyAction === 'send_now' ? 'Sending…' : "Send today's email now"}
              </button>
            </div>
          </>
        )}
        {actionMsg && <div style={{ fontSize: 13, marginTop: 10, color: TONE_COLOR[actionMsg.tone], overflowWrap: 'anywhere' }}>{actionMsg.text}</div>}
      </div>
    </>
  );
}
